/**
 * Peleas: entidad central del dominio.
 */
import { z } from 'zod';
import { CentsWireSchema, OddsBpSchema } from '../money.js';
import { RulesSnapshotSchema } from './rules.js';

export const CornerSchema = z.enum(['red', 'blue']);
export type Corner = z.infer<typeof CornerSchema>;
export const CORNERS = CornerSchema.options;

export const FightStatusSchema = z.enum(['open', 'locked', 'settled', 'cancelled']);
export type FightStatus = z.infer<typeof FightStatusSchema>;

/** Transiciones válidas de estado. `settle` exige `locked`; `lock` exige `open` (§7.3). */
export const FIGHT_TRANSITIONS: Record<FightStatus, readonly FightStatus[]> = {
  open: ['locked', 'cancelled'],
  locked: ['settled', 'cancelled'],
  settled: [],
  cancelled: [],
};

export const FightModeSchema = z.enum(['1v1']);
export type FightMode = z.infer<typeof FightModeSchema>;

/**
 * Snapshot del peleador congelado en la pelea: si el catálogo cambia, la pelea no.
 */
export const FighterSnapshotSchema = z.object({
  id: z.string().nullable(),
  name: z.string().min(1).max(80),
  crew: z.string().max(80).nullable(),
  record: z
    .object({
      wins: z.number().int().nonnegative(),
      losses: z.number().int().nonnegative(),
      draws: z.number().int().nonnegative().default(0),
    })
    .nullable(),
  photoUrl: z.string().url().nullable(),
});
export type FighterSnapshot = z.infer<typeof FighterSnapshotSchema>;

/** Métricas vivas de la pelea. Derivadas del ledger; cacheadas en memoria (§22). */
export const FightMetricsSchema = z.object({
  seq: z.number().int().nonnegative(),
  redPoolCents: CentsWireSchema,
  bluePoolCents: CentsWireSchema,
  totalPoolCents: CentsWireSchema,
  redBetCount: z.number().int().nonnegative(),
  blueBetCount: z.number().int().nonnegative(),
  betCount: z.number().int().nonnegative(),
  /** Cuotas estimadas actuales en bp (rotuladas "estimado" en UI si es pari-mutuel). */
  oddsRedBp: OddsBpSchema.or(z.literal(0)),
  oddsBlueBp: OddsBpSchema.or(z.literal(0)),
  /** Barra de tensión 0..100 (|red−blue| / total). */
  tensionPct: z.number().min(0).max(100),
  netPoolCents: CentsWireSchema,
  rakeCents: CentsWireSchema,
  houseEscrowCents: CentsWireSchema,
  lastBetAt: z.string().datetime({ offset: true }).nullable(),
});
export type FightMetrics = z.infer<typeof FightMetricsSchema>;

/** Métricas finales congeladas al liquidar: lo mostrado después == lo pagado (§8.1.2). */
export const FinalMetricsSchema = FightMetricsSchema.extend({
  settledAt: z.string().datetime({ offset: true }),
  winner: CornerSchema.nullable(),
  totalPaidCents: CentsWireSchema,
  totalRefundedCents: CentsWireSchema,
  houseResultCents: z.number().int(),
  winningBets: z.number().int().nonnegative(),
  losingBets: z.number().int().nonnegative(),
});
export type FinalMetrics = z.infer<typeof FinalMetricsSchema>;

export const FightSchema = z.object({
  id: z.string().length(26),
  orgId: z.string().min(1),
  title: z.string().min(1).max(120),
  mode: FightModeSchema,
  redFighter: FighterSnapshotSchema,
  blueFighter: FighterSnapshotSchema,
  status: FightStatusSchema,
  winner: CornerSchema.nullable(),
  rulesSnapshot: RulesSnapshotSchema,
  metrics: FightMetricsSchema,
  finalMetrics: FinalMetricsSchema.nullable(),
  version: z.number().int().positive(),
  createdBy: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  lockedAt: z.string().datetime({ offset: true }).nullable(),
  settledAt: z.string().datetime({ offset: true }).nullable(),
  cancelledAt: z.string().datetime({ offset: true }).nullable(),
});
export type Fight = z.infer<typeof FightSchema>;

/** ¿Se pueden emitir boletos? */
export function acceptsBets(fight: Pick<Fight, 'status'>): boolean {
  return fight.status === 'open';
}

export function isValidFightTransition(from: FightStatus, to: FightStatus): boolean {
  return FIGHT_TRANSITIONS[from].includes(to);
}
